import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faXTwitter,
} from '@fortawesome/free-brands-svg-icons'
import {
  faCheck,
  faCopy,
  faDownload,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  Button,
  Center,
  Group,
  Image,
  Paper,
  Radio,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { useClipboard } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'

const SOCIAL_IMAGE_URLS = {
  post: 'https://hiddengemzstorage.blob.core.windows.net/system/NewHost.Post.png',
  story:
    'https://hiddengemzstorage.blob.core.windows.net/system/Host.Story.jpg',
  square:
    'https://hiddengemzstorage.blob.core.windows.net/system/Host.Square.png',
}
const WEBSITE_URL = 'https://hiddengemz.tv'

type ImageSize = 'story' | 'post' | 'square'

interface HostSharePanelProps {
  locationId?: number | null
  locationName?: string | null
}

export function HostSharePanel({
  locationId,
  locationName,
}: HostSharePanelProps) {
  const clipboard = useClipboard({ timeout: 2000 })
  const [imageSize, setImageSize] = useState<ImageSize>('post')

  const shareUrl = locationId
    ? `${WEBSITE_URL}/locations/${locationId}`
    : WEBSITE_URL

  const shareCaption = locationName
    ? `🎉 ${locationName} is now a host on the Hidden Gemz advertising network! Businesses can advertise on our screen. Upload your ad now at ${shareUrl} #HiddenGemz #SupportLocal #LocalAdvertising`
    : `🎉 We're a host on the Hidden Gemz advertising network! Businesses can advertise on our screen. Upload your ad now at ${shareUrl} #HiddenGemz #SupportLocal #LocalAdvertising`

  const handleDownloadImage = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      window.URL.revokeObjectURL(link.href)
    } catch (err) {
      console.error('Failed to download image', err)
      notifications.show({
        title: 'Error',
        message: 'Failed to download image. Please try again.',
        color: 'red',
      })
    }
  }

  const handleCopyCaption = () => {
    clipboard.copy(shareCaption)
    notifications.show({
      title: 'Caption Copied!',
      message: 'Your caption has been copied to clipboard.',
      color: 'green',
      icon: <FontAwesomeIcon icon={faCheck} />,
      autoClose: 3000,
    })
  }

  const handleInstagramShare = () => {
    window.open('https://instagram.com', '_blank')
  }

  const handleShare = (platform: string) => {
    let url = ''
    switch (platform) {
      case 'x':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareCaption)}&url=${encodeURIComponent(shareUrl)}`
        break
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareCaption)}`
        break
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
        break
      case 'native':
        if (navigator.share) {
          navigator
            .share({ title: 'Hidden Gemz', text: shareCaption, url: shareUrl })
            .catch(console.error)
        } else {
          clipboard.copy(`${shareCaption} ${shareUrl}`)
          notifications.show({
            title: 'Copied!',
            message: 'Share text has been copied to clipboard.',
            color: 'green',
            icon: <FontAwesomeIcon icon={faCheck} />,
          })
        }
        return
    }
    window.open(url, '_blank')
  }

  return (
    <Paper
      shadow='sm'
      radius='md'
      p='xl'
      withBorder
      style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
    >
      <Stack gap='lg'>
        <Stack gap={4}>
          <Text size='lg' c='dimmed' ta='center'>
            No ads running here yet.
          </Text>
          <Title order={3} ta='center'>
            Spread the word to attract advertisers!
          </Title>
        </Stack>

        <Stack
          gap='xl'
          align='center'
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          }}
        >
          <Center>
            <Image
              src={SOCIAL_IMAGE_URLS['post']}
              radius='md'
              w='100%'
              maw={360}
              h='auto'
              fit='contain'
              alt='Hidden Gemz host graphic'
            />
          </Center>

          <Stack gap='md'>
            <Stack
              gap='sm'
              style={{
                backgroundColor: 'var(--mantine-color-blue-0)',
                padding: '0.75rem',
                borderRadius: '6px',
              }}
            >
              <Text fw={600} size='sm'>
                💡 Pro Tip
              </Text>
              <Text size='xs'>
                Take a photo of your device and share it alongside our brand
                image! Tag us @hiddengemz.tv for best reach.
              </Text>
            </Stack>

            <Stack gap='sm'>
              <Title order={4}>Image Size</Title>
              <Radio
                label='Story (1080 x 1920)'
                value='story'
                checked={imageSize === 'story'}
                onChange={() => setImageSize('story')}
                size='sm'
              />
              <Radio
                label='Square (1080 x 1080)'
                value='square'
                checked={imageSize === 'square'}
                onChange={() => setImageSize('square')}
                size='sm'
              />
              <Radio
                label='Post (1080 x 1350)'
                value='post'
                checked={imageSize === 'post'}
                onChange={() => setImageSize('post')}
                size='sm'
              />
            </Stack>

            <Button
              leftSection={<FontAwesomeIcon icon={faDownload} />}
              variant='filled'
              color='dark'
              onClick={() => {
                const filename = `hidden-gemz-${imageSize}.${imageSize === 'story' ? 'jpg' : 'png'}`
                handleDownloadImage(SOCIAL_IMAGE_URLS[imageSize], filename)
              }}
              size='sm'
            >
              Download Image
            </Button>

            <Stack gap='sm'>
              <Title order={4}>Your Caption</Title>
              <Text c='dimmed' size='xs'>
                Copy for your post
              </Text>
              <Paper
                p='md'
                withBorder
                style={{ backgroundColor: 'var(--mantine-color-white)' }}
              >
                <Text size='xs' style={{ whiteSpace: 'pre-wrap' }}>
                  {shareCaption}
                </Text>
              </Paper>
              <Button
                leftSection={
                  clipboard.copied ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : (
                    <FontAwesomeIcon icon={faCopy} />
                  )
                }
                variant={clipboard.copied ? 'filled' : 'light'}
                color={clipboard.copied ? 'green' : 'blue'}
                size='sm'
                onClick={handleCopyCaption}
              >
                {clipboard.copied ? 'Copied!' : 'Copy Caption'}
              </Button>
            </Stack>
          </Stack>
        </Stack>

        <Group gap='lg' justify='center' wrap='wrap'>
          <Tooltip label='Share on Instagram' withArrow>
            <ActionIcon
              size={60}
              radius='xl'
              variant='gradient'
              gradient={{ from: '#833ab4', to: '#fd1d1d', deg: 45 }}
              onClick={handleInstagramShare}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faInstagram} size='xl' />
            </ActionIcon>
          </Tooltip>

          <Tooltip label='Share on X (Twitter)' withArrow>
            <ActionIcon
              size={60}
              radius='xl'
              variant='filled'
              color='dark'
              onClick={() => handleShare('x')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faXTwitter} size='xl' />
            </ActionIcon>
          </Tooltip>

          <Tooltip label='Share on Facebook' withArrow>
            <ActionIcon
              size={60}
              radius='xl'
              variant='filled'
              color='blue'
              onClick={() => handleShare('facebook')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faFacebook} size='xl' />
            </ActionIcon>
          </Tooltip>

          <Tooltip label='Share on LinkedIn' withArrow>
            <ActionIcon
              size={60}
              radius='xl'
              variant='filled'
              color='blue'
              onClick={() => handleShare('linkedin')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faLinkedin} size='xl' />
            </ActionIcon>
          </Tooltip>

          <Tooltip label='Share' withArrow>
            <ActionIcon
              size={60}
              radius='xl'
              variant='light'
              color='gray'
              onClick={() => handleShare('native')}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon icon={faShareNodes} size='xl' />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Stack>
    </Paper>
  )
}
